/* eslint-disable arrow-body-style */
import { mapActions, mapState } from 'vuex';
import { toCents } from '@/utils/helpers';
import loaderMixin from '@/mixins/loader';
import Contract from '@/models/contract';
import { MODALS } from '@/components/app/app-modals/app-modals';

export default {
  mixins: [
    loaderMixin,
  ],
  props: {
    slim: {
      type: Boolean,
      default: false,
    },
    contract: {
      type: Object,
      required: true,
    },
    orderBook: {
      type: Object,
      required: true,
    },
    buyOffers: {
      type: Array,
      default: [],
    },
    sellOffers: {
      type: Array,
      default: [],
    },
    operationValue: {
      type: String,
      default: 'yes',
      validator: value => ['no', 'yes'].indexOf(value) >= 0,
    },
    operationType: {
      type: String,
      default: 'buy',
    },
  },
  data() {
    return {
      showAll: false,
      showAllCut: 5,
      hoveredIndex: null,
      leftPricesUnavailable: true,
      rightPricesUnavailable: true,
      sideActive: 'left',
    };
  },
  watch: {
    orderBook() {
      this.leftPricesUnavailable = true;
      this.rightPricesUnavailable = true;
    },
  },
  computed: {
    ...mapState('trade', ['tradeOptions']),
    isHoverable() {
      if (!this.contractModel.mode) return true;
      if (this.contractModel.mode === this.tradeOptions.operationValue) return true;
      return this.contractModel.userOpenOrders === 0 && this.contractModel.userQuantity === 0;
    },
    contractModel() {
      return new Contract(this.contract);
    },
    tableModifiers() {
      return {
        'order-book-sushi__table': true,
        'order-book-sushi__table--slim': this.slim,
      };
    },
    leftSideNoOffersModifiers() {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__unavailable': true,
        'order-book-sushi__unavailable--active': (this.sideActive === 'left'),
      };
    },
    rightSideNoOffersModifiers() {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__unavailable': true,
        'order-book-sushi__unavailable--right': true,
        'order-book-sushi__unavailable--active': (this.sideActive === 'right'),
      };
    },
    leftSideHeaderModifiers() {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__header-cell': true,
        'order-book-sushi__header-cell--with-avail-matches': true,
        'order-book-sushi__header-cell--active': (this.sideActive === 'left'),
      };
    },
    rightSideHeaderModifiers() {
      return {
        'order-book-sushi__side': true,
        'order-book-sushi__header-cell': true,
        'order-book-sushi__header-cell--with-avail-matches': true,
        'order-book-sushi__header-cell--active': (this.sideActive === 'right'),
      };
    },
    isSharesOwned() {
      return this.contract.userQuantity > 0 || this.buyOffers.length > 0;
    },
    operationValueText() {
      return this.operationValue.charAt(0).toUpperCase() + this.operationValue.slice(1);
    },
    operationTypeLeftText() {
      return 'Buy';
    },
    operationTypeRightText() {
      return 'Sell';
    },
    operationValueRightText() {
      // const opposite = this.operationValue === 'yes' ? 'No' : 'Yes';
      return this.operationValueText;
    },
    pricesGrouped() {
      const prices = [];
      const yesOrders = this.orderBook ? this.orderBook.yesOrders || [] : [];
      const noOrders = this.orderBook ? this.orderBook.noOrders || [] : [];

      let leftPrices = [...yesOrders];
      let rightPrices = [...noOrders];
      let leftField = 'pricePerShare';
      let rightField = 'costPerShareYes';
      let leftOffers = this.sellOffers;
      let rightOffers = this.buyOffers;
      this.sideActive = 'left';

      if (this.isSharesOwned) {
        if ((this.operationValue === 'yes' && this.operationType === 'buy') || (this.operationType === 'sell' && this.operationValue === 'no')) {
          leftPrices = [...yesOrders];
          rightPrices = [...noOrders];
          leftField = 'pricePerShare';
          rightField = 'costPerShareYes';
          leftOffers = this.sellOffers;
          rightOffers = this.buyOffers;
          if (this.operationType === 'sell') {
            this.sideActive = 'right';
            leftPrices = [...noOrders];
            rightPrices = [...yesOrders];
            leftOffers = this.sellOffers;
            rightOffers = this.buyOffers;
            leftField = 'pricePerShare';
            rightField = 'costPerShareNo';
          }
        } else {
          leftPrices = [...noOrders];
          rightPrices = [...yesOrders];
          leftField = 'pricePerShare';
          rightField = 'costPerShareNo';
          leftOffers = this.sellOffers;
          rightOffers = this.buyOffers;
          if (this.operationType === 'sell') {
            this.sideActive = 'right';
            leftPrices = [...yesOrders];
            rightPrices = [...noOrders];
            leftField = 'pricePerShare';
            rightField = 'costPerShareYes';
            leftOffers = this.sellOffers;
            rightOffers = this.buyOffers;
          }
        }
      } else if (this.operationValue === 'no') {
        rightField = 'costPerShareNo';
        leftPrices = [...noOrders];
        rightPrices = [...yesOrders];
      }

      const leftOffersField = leftField;
      const rightOffersField = rightField;

      const max = leftPrices.length > rightPrices.length ? leftPrices.length : rightPrices.length;

      for (let i = 0; i < max; i += 1) {
        if (leftPrices[i]) {
          this.leftPricesUnavailable = false;
        }
        if (rightPrices[i]) {
          this.rightPricesUnavailable = false;
        }

        prices.push({
          index: i,
          buyPrice: leftPrices[i] ? toCents(leftPrices[i][leftField]) : null,
          buyQuantity: leftPrices[i] ? leftPrices[i].quantity : null,
          buyOwned: leftPrices[i] ?
            leftOffers.find(s => s.pricePerShare === leftPrices[i][leftOffersField])
            !== undefined : false,
          sellPrice: rightPrices[i] ? toCents(rightPrices[i][rightField]) : null,
          sellQuantity: rightPrices[i] ? rightPrices[i].quantity : null,
          sellOwned: rightPrices[i] ?
            rightOffers.find(s => s.pricePerShare === rightPrices[i][rightOffersField])
              !== undefined : false,
        });
      }
      return prices;
    },
    pricesOwned() {
      return this.pricesGrouped.find(item => item.buyOwned || item.sellOwned);
    },
    firstItemForActiveSideOwned() {
      return this.pricesGrouped.find((item) => {
        return (this.sideActive === 'right') ? item.sellOwned : item.buyOwned;
      });
    },
    showAllItemsNotice() {
      return this.pricesGrouped.length > this.showAllCut;
    },
    itemsToDisplay() {
      return this.showAll ? this.pricesGrouped : this.pricesGrouped.slice(0, this.showAllCut);
    },
  },
  methods: {
    ...mapActions('popup', ['setGenericSmallPopup']),
    ...mapActions('trade', ['getOrderBookTradeInfo', 'toggleTradeOperation']),
    getTradeType(operationType, operationValue) {
      if (operationType === 'buy' && operationValue === 'no') return 0;
      if (operationType === 'buy' && operationValue === 'yes') return 1;
      if (operationType === 'sell' && operationValue === 'no') return 2;
      if (operationType === 'sell' && operationValue === 'yes') return 3;
      return 1;
    },
    onMouseOver(index, side) {
      if (!(this.sideActive === side)) return;
      this.hoveredIndex = index;
    },
    onMouseLeave(side) {
      if (!(this.sideActive === side)) return;
      this.hoveredIndex = null;
    },
    hoverQuantityClassModifier(owned, index, side) {
      const isSideActive = this.sideActive === side && this.isHoverable;
      return {
        'order-book-sushi__body-cell--owned': owned,
        'order-book-sushi__body-cell--hovered': (isSideActive && this.hoveredIndex === index),
        'order-book-sushi__body-cell--clickable': isSideActive,
        'order-book-sushi__body-cell--inactive': !isSideActive,
      };
    },
    hoverPriceClassModifier(owned, index, side) {
      const isSideActive = this.sideActive === side && this.isHoverable;
      return {
        'order-book-sushi__body-cell--price': true,
        'order-book-sushi__body-cell--hovered': (isSideActive && this.hoveredIndex === index),
        'order-book-sushi__body-cell--clickable': isSideActive,
        'order-book-sushi__body-cell--inactive': !isSideActive,
      };
    },
    showTradeInfo(item, operationType, operationValue, side) {
      const price = (side === 'right') ? item.sellPrice : item.buyPrice;
      const payload = {
        tradeType: this.getTradeType(operationType, operationValue),
        contractId: this.contract.contractId,
        price,
      };
      this.loaderAdd(document.body);
      this.getOrderBookTradeInfo(payload)
        .then((data) => {
          setTimeout(() => {
            this.loaderRemove(document.body);
            this.setGenericSmallPopup({
              name: MODALS.SMALL_OFFERS_INDICATOR,
              options: {
                items: data,
                operationType,
                price,
              },
            });
          }, 400);
        })
        .catch(() => this.loaderRemove(document.body));
    },
    handleClick(item, isMine, operationType, operationValue, side) {
      if (
        !isMine &&
        this.firstItemForActiveSideOwned &&
        item.index > this.firstItemForActiveSideOwned.index) {
        return;
      }
      if (isMine) {
        this.showTradeInfo(item, operationType, operationValue, side);
        return;
      }
      let quantity = this.pricesGrouped.slice(0, item.index + 1).reduce((prev, curr) => {
        return prev + curr.buyQuantity;
      }, 0);
      let price = this.pricesGrouped[item.index].buyPrice;

      if (side === 'right') {
        quantity = this.pricesGrouped.slice(0, item.index + 1).reduce((prev, curr) => {
          return prev + curr.sellQuantity;
        }, 0);
        price = this.pricesGrouped[item.index].sellPrice;
      }
      if (price === null) return;
      if (operationType === 'sell' && quantity > this.contract.userQuantity) {
        quantity = this.contract.userQuantity;
      }
      this.$emit('click', {
        quantity,
        price,
      });
    },
    onClick(item, isMine, side) {
      if (this.sideActive === side) {
        this.handleClick(item, isMine, this.operationType, this.operationValue, this.sideActive);
      } else if (isMine) {
        if (side === 'left') {
          this.showTradeInfo(item, this.operationTypeLeftText.toLowerCase(), this.operationValueText.toLowerCase(), side);
        } else {
          this.showTradeInfo(item, this.operationTypeRightText.toLowerCase(), this.operationValueRightText.toLowerCase(), side);
        }
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/order-book-sushi/order-book-prices-sushi/order-book-prices-sushi.js